import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { SEB_TEST_DATA } from '../../data/sebTestData';
import { MEGA_MAPPING } from '../../data/megaMapping';
import { getMegaTypeProfile } from '../../data/megaTypeProfiles';
import { getResultDetail, saveMegaResult } from '../../client';
import { useContent } from '../../content';
import ScoreRadar from '../../components/charts/ScoreRadar';
import './MegaTest.css';

/**
 * Result Component: Displays dual analysis report (SEB + Behavioral)
 * Redesigned for a premium, high-end feel.
 */
const Result = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const { getC, loaded: contentLoaded } = useContent(['love']);
    const [dualResult, setDualResult] = useState(null);
    const [detailError, setDetailError] = useState('');
    const lang = i18n.language === 'en' ? 'en' : 'ko';

    const simplifyText = (value, max = 160) => {
        const text = String(value || '').replace(/\s+/g, ' ').trim();
        if (!text) return '';
        if (text.length <= max) return text;
        return text.slice(0, max) + '...';
    };

    const toPlainLoveKorean = (value) => {
        const text = String(value || '').trim();
        if (!text || lang !== 'ko') return text;
        return text
            .replace(/구조를 가지고 있습니다/g, '성향입니다')
            .replace(/할 수 있습니다/g, '합니다')
            .replace(/될 수 있습니다/g, '됩니다')
            .replace(/\s{2,}/g, ' ')
            .trim();
    };

    const calculateNewResult = (savedAnswers) => {
        let answers = {};
        try { answers = JSON.parse(savedAnswers); } catch (e) { navigate('/mega-test'); return; }

        let sebScores = { S: 0, E: 0, F: 0 };
        let sebMaxScores = { S: 0, E: 0, F: 0 };
        SEB_TEST_DATA.questions.forEach(q => {
            const axis = q.axis;
            const weight = q.weight;
            const answerIndex = answers[q.id];
            if (answerIndex !== undefined) {
                sebScores[axis] += answerIndex * weight;
                sebMaxScores[axis] += 3 * weight;
            }
        });

        const getL = (a) => {
            const m = sebMaxScores[a];
            const p = m ? Math.round((sebScores[a] / m) * 100) : 0;
            return p >= 67 ? 'H' : (p >= 34 ? 'M' : 'L');
        };
        const typeCode = `${getL('S')}${getL('E')}${getL('F')}`;
        const sebData = SEB_TEST_DATA.results.find(r => r.type_code === typeCode);

        const behAxes = ['decision_style', 'emotional_response', 'value_priority', 'consistency', 'self_vs_other', 'reflection', 'flexibility', 'action_initiation', 'perception_gap'];
        let behRaw = {};
        behAxes.forEach(a => behRaw[a] = 0);
        Object.entries(answers).forEach(([qId, opt]) => {
            const m = MEGA_MAPPING[qId];
            if (m && m[opt]) {
                Object.entries(m[opt]).forEach(([a, v]) => { if (behRaw[a] !== undefined) behRaw[a] += v; });
            }
        });

        const behNorm = {};
        behAxes.forEach(a => { behNorm[a] = Math.max(0, Math.min(100, 50 + (behRaw[a] * 8))); });

        const groupA = Math.round((behNorm.decision_style + behNorm.flexibility + behNorm.action_initiation) / 3);
        const groupB = Math.round((behNorm.self_vs_other + behNorm.reflection + behNorm.perception_gap) / 3);

        const res = {
            seb: { ...sebData, scores: sebScores, maxScores: sebMaxScores },
            behavior: { scores: behNorm, groupA, groupB }
        };
        setDualResult(res);

        const save = async () => {
            const sp = Math.round((sebScores.S / (sebMaxScores.S || 1)) * 100);
            const ep = Math.round((sebScores.E / (sebMaxScores.E || 1)) * 100);
            const fp = Math.round((sebScores.F / (sebMaxScores.F || 1)) * 100);
            const tc = sebData?.type_code || typeCode;
            const profile = getMegaTypeProfile(tc);
            const payload = {
                test_type: "mega",
                result_label: profile?.name || sebData?.name || tc,
                summary: { mbti: tc, score_seb: { S: sp, E: ep, F: fp }, score_beh: { A: groupA, B: groupB } },
                payload: {
                    seb: { type_code: tc, name: profile?.name || sebData?.name, scores: { S: sp, E: ep, F: fp } },
                    behavior: { scores: behNorm, groupA, groupB },
                    answers
                }
            };
            try { await saveMegaResult(payload); } catch (e) { console.error(e); }
        };
        save();
    };

    useEffect(() => {
        const rehydrate = (data) => {
            if (!data) return null;
            const dbD = data.payload || data;
            const iD = dbD.payload || dbD;
            const tc = iD.seb?.type_code || iD.type_code || dbD.summary?.mbti || iD.mbti || (typeof dbD.result_label === 'string' && /^[HML]{3}$/.test(dbD.result_label) ? dbD.result_label : '');
            const sd = tc ? SEB_TEST_DATA.results.find(r => r.type_code === tc) : null;
            const profile = tc ? getMegaTypeProfile(tc) : null;
            return {
                ...iD,
                seb: { 
                    ...(sd || {}), 
                    ...(iD.seb || {}), 
                    type_code: tc || iD.seb?.type_code || 'MMM', 
                    name: profile?.name || iD.seb?.name || sd?.name || (tc ? `${tc} 유형` : '결과') 
                },
                behavior: iD.behavior || dbD.behavior || { 
                    scores: iD.scores?.beh || iD.behavior?.scores || {}, 
                    groupA: iD.summary?.score_beh?.A || iD.behavior?.groupA || 50, 
                    groupB: iD.summary?.score_beh?.B || iD.behavior?.groupB || 50 
                }
            };
        };

        const fetchD = async () => {
            if (!id) return;
            setDetailError('');
            if (id.startsWith('guest_') || isNaN(Number(id))) {
                const p = JSON.parse(localStorage.getItem('pendingResults') || '[]');
                const g = p.find(item => item.payload?.id === id || item.payload?.payload?.id === id);
                if (g) { const d = rehydrate(g.payload); if (d) { setDualResult(d); return; } }
            }
            try {
                const d = await getResultDetail(id);
                if (d) { const cd = rehydrate(d); if (cd) setDualResult(cd); }
                else throw new Error();
            } catch { setDetailError('결과를 불러오지 못했습니다. 데이터가 없거나 로그인 세션이 만료되었을 수 있습니다.'); }
        };

        if (id) fetchD();
        else {
            const sa = localStorage.getItem('megaTestAnswers');
            if (sa) calculateNewResult(sa);
            else navigate('/mega-test');
        }
    }, [id, navigate]);

    if (id && detailError) return (
        <div className="loading-container" style={{ textAlign: 'center' }}>
            <p style={{ maxWidth: 450, margin: '0 auto' }}>{detailError}</p>
            <button className="next-button" style={{ marginTop: '1.5rem' }} onClick={() => navigate('/mypage')}>마이페이지로 가기</button>
        </div>
    );

    if (!dualResult) return (
        <div className="loading-container">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }} className="loading-spinner" />
            <p>결과를 분석하고 있습니다...</p>
        </div>
    );

    const { seb, behavior } = dualResult;
    const tc = seb?.type_code || dualResult?.type_code || dualResult?.summary?.mbti || '';
    const profile = getMegaTypeProfile(tc);

    const getF = (path, fallback = '') => {
        if (!seb) return fallback;
        if (profile) {
            const v = path.split('.').reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), profile);
            if (typeof v === 'string' && v.trim()) return v;
        }
        if (tc) {
            const cv = getC(`love.mega_test.results.${tc}.${path}`);
            if (typeof cv === 'string' && cv.trim()) return cv;
        }
        const lv = path.split('.').reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), seb);
        if (typeof lv === 'string' && lv.trim()) return lv;

        if (path === 'definition' || path === 'summary') return seb.summary || seb.description || fallback;
        if (path === 'caution') return (Array.isArray(seb.advice?.content) ? seb.advice.content.join(' ') : (seb.action_plan || fallback));
        
        return fallback;
    };

    const sp = seb.maxScores ? (seb.scores.S / seb.maxScores.S) * 100 : (seb.scores?.S || 50);
    const ep = seb.maxScores ? (seb.scores.E / seb.maxScores.E) * 100 : (seb.scores?.E || 50);
    const fp = seb.maxScores ? (seb.scores.F / seb.maxScores.F) * 100 : (seb.scores?.F || 50);

    const sections = [
        { title: '유형 정의', text: getF('definition', getF('summary')) },
        { title: '심층 심리 분석', text: seb.depth_psychology?.content || '' },
        { title: '사회적 역할', text: getF('social_view', getF('social_role')) },
        { title: '오해와 맹점', text: getF('misunderstanding', getF('blind_spots')) },
        { title: '주의할 점', text: getF('caution') },
        { title: '중요 가치', text: getF('core_value', getF('academic_report.core_driver')) }
    ].map(s => ({ ...s, text: toPlainLoveKorean(s.text).trim() })).filter(s => s.text);

    return (
        <div className="result-container">
            <div className="content-wrapper">
                <motion.header initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="top-header">
                    <div className="badge">MEGA SYNERGY</div>
                    <h1>메가 시너지 결과 리포트</h1>
                </motion.header>



                    <motion.div key="seb" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <section className="seb-header">
                            <h2 className="type-name">{seb.name || tc}</h2>
                        </section>

                        <div className="premium-card score-card" style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 1fr', gap: '2rem', alignItems: 'center' }}>
                            <div className="radar-wrapper">
                                <ScoreRadar scores={{ max: 100, axes: [{ key: 'S', label: `정서 안정(${tc[0] || ''})`, score: sp }, { key: 'E', label: `공감 능력(${tc[1] || ''})`, score: ep }, { key: 'F', label: `목표 추진(${tc[2] || ''})`, score: fp }] }} title="성험 지표" />
                            </div>
                            <div className="bars-wrapper">
                                {[ { l: `S (정서 안정) - ${tc[0] || ''}`, v: sp, c: 's-bar' }, { l: `E (공감 능력) - ${tc[1] || ''}`, v: ep, c: 'e-bar' }, { l: `B (목표 추진) - ${tc[2] || ''}`, v: fp, c: 'f-bar' } ].map(i => (
                                    <div key={i.l} className="score-item">
                                        <div className="score-info"><span>{i.l}</span><span>{Math.round(i.v)}%</span></div>
                                        <div className="progress-bg">
                                            <motion.div initial={{ width: 0 }} animate={{ width: `${i.v}%` }} className={`progress-fill ${i.c}`} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="module-section-grid">
                            {sections.map(s => (
                                <div key={s.title} className="module-section-card">
                                    <h4>{s.title}</h4>
                                    <p style={{ whiteSpace: 'pre-line' }}>{s.text}</p>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                <div className="main-actions">
                    <button className="home-btn" onClick={() => navigate('/')}>홈으로</button>
                    <button className="restart-btn" onClick={() => { localStorage.removeItem('megaTestAnswers'); navigate('/mega-test'); }}>테스트 다시하기</button>
                </div>
            </div>
        </div>
    );
};

export default Result;
